import { formatPlate } from "@szerviz/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp, useFeature } from "../context";
import type { QuoteNotice } from "../data";

const POLL_MS = 30_000;
const seenKey = (workshopId: string) => `szerviz.quoteSeen.${workshopId}`;
const readSeen = (workshopId: string): string | null => {
  try {
    return globalThis.localStorage?.getItem(seenKey(workshopId)) ?? null;
  } catch {
    return null;
  }
};
const writeSeen = (workshopId: string, iso: string) => {
  try {
    globalThis.localStorage?.setItem(seenKey(workshopId), iso);
  } catch {
    // storage unavailable: the notice may show again after a reload, nothing worse
  }
};

/** Fired when a customer's answer arrives, so an open work order reloads itself. */
export const QUOTE_ANSWERED_EVENT = "szerviz:quote-answered";

/**
 * A gentle notice in the corner when a customer answered a quote on their link:
 * green when something was approved ("the repair can start"), orange when nothing was.
 * Checked every 30 seconds and when the window comes back into view.
 */
export function QuoteNotices() {
  const { t } = useTranslation();
  const { store, workshop, go } = useApp();
  const on = useFeature("quotes");
  const [notices, setNotices] = useState<QuoteNotice[]>([]);
  const since = useRef<string>(readSeen(workshop.id) ?? new Date().toISOString());
  const shown = useRef(new Set<string>());

  const check = useCallback(async () => {
    try {
      const fresh = (await store.recentQuoteAnswers(workshop.id, since.current)).filter((n) => !shown.current.has(n.quoteId));
      if (fresh.length === 0) return;
      for (const n of fresh) shown.current.add(n.quoteId);
      setNotices((list) => [...fresh, ...list]);
      globalThis.dispatchEvent?.(new Event(QUOTE_ANSWERED_EVENT));
    } catch {
      // offline for a moment: try again next time
    }
  }, [store, workshop.id]);

  useEffect(() => {
    if (!on) return;
    if (!readSeen(workshop.id)) writeSeen(workshop.id, since.current);
    void check();
    const timer = setInterval(() => void check(), POLL_MS);
    const onVisible = () => globalThis.document?.visibilityState !== "hidden" && void check();
    globalThis.addEventListener?.("focus", onVisible);
    globalThis.document?.addEventListener?.("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      globalThis.removeEventListener?.("focus", onVisible);
      globalThis.document?.removeEventListener?.("visibilitychange", onVisible);
    };
  }, [on, check, workshop.id]);

  const dismiss = (n: QuoteNotice) => {
    setNotices((list) => {
      const rest = list.filter((x) => x.quoteId !== n.quoteId);
      // once all are closed, older answers are not shown again
      if (rest.length === 0) {
        const newest = [n, ...list].map((x) => x.answeredAt).sort().at(-1)!;
        since.current = newest;
        writeSeen(workshop.id, newest);
      }
      return rest;
    });
  };

  if (!on || notices.length === 0) return null;
  return (
    <div className="notices" aria-live="polite">
      {notices.map((n) => {
        const who = n.customerName ?? t("quote.notice.someone");
        const plate = n.plateNumber ? formatPlate(n.plateNumber, n.plateCountry ?? "HU") : n.number;
        return (
          <div key={n.quoteId} className={`notice${n.approved === 0 ? " none" : ""}`} role="status">
            <p>
              {n.approved > 0
                ? t("quote.notice.approved", { customer: who, plate, approved: n.approved, total: n.total })
                : t("quote.notice.none", { customer: who, plate })}
            </p>
            <div className="actions">
              <button
                type="button"
                className="primary"
                onClick={() => {
                  dismiss(n);
                  go("workOrders", n.workOrderId);
                }}
              >
                {t("quote.notice.open")}
              </button>
              <button type="button" onClick={() => dismiss(n)}>
                {t("quote.notice.close")}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
